import { addElement, empty, textWidth } from 'sesajs/dom'
import { isValidId } from 'sesajs/string'
import { Modal } from 'sesajs/ui'

import { cssKo, cssOk, cssOkPartial } from 'src/legacy/core/styles'
import m from 'src/lib/i18n/msgCore'

import 'sesajs/ui.css'

export type GetOverflowingOutResult = {
  bottom: number
  /** Les conteneurs absolute|fixed coupables du débordement */
  culprits: HTMLElement[]
  hasOverflowOut: boolean
  /** Les enfants qui débordent en bas */
  offendersBottom: HTMLElement[]
  /** Les enfants qui débordent à droite */
  offendersRight: HTMLElement[]
  right: number
}

/**
 * Retourne le premier parent ayant un positionnement absolute ou fixed (en s'arrêtant à stopAt)
 * @param innerElt L'élément dont on analyse les ancêtres
 * @param [stopAtElt] L'ancêtre sur lequel s'arrêter (body par défaut)
 */
export function getAbsoluteOrFixedAncestor (innerElt: Element, stopAtElt: HTMLElement = document.body): HTMLElement | null {
  let current: Element | null = innerElt.parentElement
  while (current && current !== stopAtElt) {
    const { position } = getComputedStyle(current)
    if (position === 'absolute' || position === 'fixed') {
      return current as HTMLElement
    }
    current = current.parentElement
  }

  return null
}

/**
 * Un getElementById amélioré, qui râle en console s’il ne trouve pas l’élément (ou s’il le trouve et qu’il devrait pas), mais aussi si y’en a plusieurs
 * Il ne devrait jamais y avoir besoin d’utiliser cette fonction car on ne devrait jamais imposer d’id.
 * Si vous avez besoin d’un élément gardez une référence dessus (avec `const elt = addElement(…)`)
 * sans ajouter d’id aux éléments.
 * @obsolete
 * @param id
 * @param [shouldExists=true] Avec undefined|true il râle s’il ne trouve pas, avec false il râle s’il trouve, et avec tout le reste il reste muet (donc passer null pour le faire taire indépendamment du résultat)
 */
export function getElement (id: HTMLElement | string, shouldExists: boolean | null = true): Node | null {
  if (id instanceof HTMLElement) {
    console.error(new Error('j3pElement appelé avec un HTMLElement'))
    return id
  }
  if (typeof id !== 'string') {
    console.error(TypeError('getElement ne traite que des string'))
    return null
  }
  // on utilise querySelectorAll plutôt que getElementById parce que l’on veut savoir s’il y en a plusieurs
  // Mais y’a des sections qui affectent n’importe quoi comme id (qui commence par un chiffre ou contient des caractères invalides comme des parenthèses)
  // => ça plante avec du `xxx is not a valid selector.`, d’où le test préalable (y’avait un try/catch dans le commit 604d3284)
  // cf https://www.w3.org/TR/1999/REC-html401-19991224/types.html#type-name
  let elts: HTMLElement[] | NodeList
  if (isValidId(id)) {
    elts = Array.from(document.querySelectorAll('#' + id))
  } else {
    const elt = document.getElementById(id)
    elts = elt ? [elt] : [] // si y’en a pas on veut une liste vide et pas [null]
  }

  if (elts.length) {
    if (elts.length > 1) console.error(new Error(`Il y a ${elts.length} éléments dans le dom avec l’id ${id}\u202F!`))
    else if (shouldExists === false) console.error(new Error(`Il y a déjà un élément d’id ${id} dans le DOM`))
    return elts[0] as HTMLElement
  }

  // rien trouvé…
  if (!elts.length && shouldExists === true) {
    // Il faut le signaler
    console.error(new Error('Aucun élément ' + id + ' dans le document courant'))
  }
  return null
}

/**
 * Retourne ce qui déborde à droite et en bas de ct et serait tronqué (enfant d'un absolute|fixed)
 * En horizontal on ne remonte pas de coupable si la zone scrollable permet d'accéder à tous les éléments.
 * En vertical on ne veut jamais de scroll (il devrait être sur la page)
 * @param ct
 */
export function getOverflowingOut (ct: HTMLElement, filter?: (elt: HTMLElement) => boolean): GetOverflowingOutResult {
  let { bottom, left, right } = ct.getBoundingClientRect()

  const scrollableRight = left + ct.scrollWidth

  const offendersRight: HTMLElement[] = []
  const offendersBottom: HTMLElement[] = []
  const culprits: HTMLElement[] = []

  // tolérance sub-pixel : un débordement d'1px ou moins est invisible (arrondis de layout)
  const tolerance = 1

  for (const node of ct.querySelectorAll('*')) {
    const elt = node as HTMLElement

    // Éléments ignorés par l'appelant (ex. ace, tailles délirantes)
    if (filter?.(elt) === true) continue

    // Les descendants d'un svg vivent dans son système de coordonnées utilisateur ;
    // tant que le svg clippe (overflow ≠ visible : hidden, auto, scroll, clip —
    // défaut UA overflow: hidden pour svg:not(:root)),
    // un débordement interne — fréquent et transitoire pendant le rendu MathGraph32 par ex —
    // n'est pas visible et ne doit pas être rapporté.
    // De même, un clip-path sur l'élément ou un ancêtre restreint le rendu visible
    // à sa région : un débordement de bbox n'y est pas visible
    // (ex: <g clip-path="url(#…)" de Blockly).
    // Le svg lui-même reste évalué (son bounding box est pertinent).
    // Vérifie clip-path sur l'élément lui-même
    let isClipped = getComputedStyle(elt).clipPath !== 'none'
    if (!isClipped && elt.tagName.toLowerCase() !== 'svg') {
      let ancestor: Element | null = elt.parentElement
      while (ancestor && ancestor !== ct) {
        if (ancestor.tagName.toLowerCase() === 'svg') {
          isClipped = getComputedStyle(ancestor).overflow !== 'visible'
          break
        }
        if (getComputedStyle(ancestor).clipPath !== 'none') {
          isClipped = true
          break
        }
        ancestor = ancestor.parentElement
      }
    }
    if (isClipped) continue

    const r = elt.getBoundingClientRect()

    const overflowsRight = r.right > Math.max(scrollableRight, right) + tolerance
    const overflowsBottom = r.bottom > bottom + tolerance
    if (!overflowsRight && !overflowsBottom) continue

    // ça déborde, mais si y'a pas de parent absolute ou que c'est un Panel on ignore
    const culprit = getAbsoluteOrFixedAncestor(elt, ct)
    if (!culprit || culprit.classList.contains('smPanel')) continue

    // faut le noter
    culprits.push(elt)
    if (overflowsRight) offendersRight.push(elt)
    if (overflowsBottom) offendersBottom.push(elt)

    // et mettre à jour le right/bottom à retourner
    if (r.right > right) right = r.right
    if (r.bottom > bottom) bottom = r.bottom
  }

  return {
    bottom,
    culprits,
    hasOverflowOut: offendersRight.length > 0 || offendersBottom.length > 0,
    offendersBottom,
    offendersRight,
    right
  }
}

export const isInputElement = (e: unknown) : e is HTMLInputElement => e != null && typeof e === 'object' && 'tagName' in e && e.tagName === 'INPUT'

interface NormalizedContainers {
  errorsContainer: HTMLDivElement
  mainContainer: HTMLDivElement
}

/**
 * Vide container et lui ajoute div.errors et div.sesaparcours qu’il retourne
 * @param container
 */
export function normalizeContainer (container: HTMLElement | string): NormalizedContainers {
  const ct = typeof container === 'string' ? document.getElementById(container) : container
  if (ct == null) throw new Error('Container manquant')
  if (!document.body.contains(ct)) throw new Error('Container invalide')
  empty(ct)
  const mainContainer = addElement(ct, 'div', { className: 'sesaparcours' })
  const errorsContainer = addElement(ct, 'div', { className: 'errors' })
  return { errorsContainer, mainContainer }
}

/**
 * Redimensionne l'input en fonction de son contenu
 * @param input
 */
export function resetInputSize (input: HTMLInputElement): void {
  const maxLength = Number(input.maxLength)
  if (maxLength === -1 || (maxLength > 0 && input.value.length <= maxLength)) {
    const { fontFamily, fontSize } = getComputedStyle(input)
    const texteZone = input.value.replaceAll(' ', '.')
    const newTailleZone = Math.max(textWidth(texteZone, fontFamily, fontSize), 5)
    input.style.width = (newTailleZone + 14) + 'px'
  }
}

/**
 * Ajoute ou retire les classes ok|ko de elt suivant isOk
 * @param elt
 * @param isOk
 */
export function setOk (elt: HTMLElement, isOk: boolean | null = null) {
  elt.classList.remove(cssOk, cssOkPartial, cssKo)
  if (isOk) {
    elt.classList.add(cssOk)
  } else if (isOk === false) {
    elt.classList.add(cssKo)
  }
  // si null on retire ok|ko sans rien ajouter
}

/**
 * Affecte la classe okPartial (et vire ok|ko si y'avait)
 * @param elt
 */
export function setOkPartial (elt: HTMLElement) {
  setOk(elt, null)
  elt.classList.add(cssOkPartial)
}

/**
 * Affiche une erreur en modale
 */
export function showError (error: unknown): void {
  if (error instanceof Error) console.error(error)
  const modal = new Modal({ className: 'sesaparcours', title: m.erreurSurvenue })
  const content = error instanceof Error ? error.message : String(error)
  modal.addElement('p', { className: 'error', content })
  modal.show()
}
